import { allocationSummary } from "@/lib/roomAllocations";
import { roomCountLabel } from "@/lib/quoteLabelUtils";
import { RichTextDisplay } from "@/components/RichTextField";
import { MapPin, Clock, Palmtree, UtensilsCrossed, Bus, Coffee, Hotel, Compass, Plane, Ticket, Check, BedDouble, CalendarRange, Globe, X } from "lucide-react";
import { getStorageUrl } from "@/lib/storageUtils";
import type { Tables } from "@/integrations/supabase/types";
import { formatStaffCategory } from "@/lib/infrastructureFees";
import { calculateAccommodationNights, calculateUniversalFeeDays } from "@/lib/itineraryCostUtils";

type TimeSlot = {
  label: string;
  start: string;
  end: string;
};

export type ItineraryItem = Omit<Tables<"itinerary_items">, 'selected_time_slot'> & {
  manual_image_url?: string | null;
  secondary_image_url?: string | null;
  image_url_3?: string | null;
  image_url_4?: string | null;
  website_url?: string | null;
  products?: { hero_image_url: string | null; product_name: string; secondary_image_url?: string | null; image_url_3?: string | null; image_url_4?: string | null } | null;
  suppliers?: { hero_image_url: string | null; name: string; website?: string | null; secondary_image_url?: string | null; image_url_3?: string | null; image_url_4?: string | null } | null;
  room_rate?: { room_type: string | null; description: string | null; notes: string | null } | null;
  selected_time_slot?: TimeSlot | null;
};

interface ActivityCardProps {
  item: ItineraryItem;
}

const getItemTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    accommodation: "bg-gradient-to-br from-blue-500 to-blue-700",
    transfer: "bg-gradient-to-br from-green-500 to-green-700",
    activity: "bg-gradient-to-br from-orange-500 to-orange-700",
    meal: "bg-gradient-to-br from-purple-500 to-purple-700",
    flight: "bg-gradient-to-br from-indigo-500 to-indigo-700",
    tour: "bg-gradient-to-br from-pink-500 to-pink-700",
    insurance: "bg-gradient-to-br from-emerald-500 to-emerald-700",
  };
  return colors[type] || "bg-gradient-to-br from-gray-500 to-gray-700";
};

const getDefaultIcon = (iconId: string) => {
  const iconMap: Record<string, any> = {
    'default-free-time': Palmtree,
    'default-meal': UtensilsCrossed,
    'default-transfer': Bus,
    'default-other': Coffee,
    'default-accommodation': Hotel,
    'default-tours': Compass,
    'default-flights': Plane,
    'default-activities': Ticket,
  };
  return iconMap[iconId] || Coffee;
};

const getIconColor = (iconId: string) => {
  const colorMap: Record<string, string> = {
    'default-free-time': 'text-blue-500',
    'default-meal': 'text-orange-500',
    'default-transfer': 'text-green-500',
    'default-other': 'text-purple-500',
    'default-accommodation': 'text-cyan-500',
    'default-tours': 'text-emerald-500',
    'default-flights': 'text-sky-500',
    'default-activities': 'text-pink-500',
  };
  return colorMap[iconId] || 'text-purple-500';
};

// Format time from "19:00" to "19h00"
const formatTimeSlot = (time: string) => {
  return time.replace(':', 'h');
};

export function ActivityCard({ item }: ActivityCardProps) {
  // Priority 1: manual image (custom item)
  const manualImagePath = item.manual_image_url as string | null;
  
  // Priority 2: product image (linked DB item)
  const productImagePath = item.products?.hero_image_url || null;
  
  // Priority 3: supplier image (fallback)
  const supplierImagePath = item.suppliers?.hero_image_url || null;
  
  const rawImagePath = manualImagePath || productImagePath || supplierImagePath || null;
  const imageUrl = rawImagePath ? getStorageUrl(rawImagePath) : null;

  // Determine display title and description
  const isCustomItem = item.item_type === 'custom';
  const baseTitle = isCustomItem 
    ? item.custom_title 
    : (item.products?.product_name || item.title || item.custom_title || item.location || item.item_type.replace("_", " "));
  
  // Add occupancy suffix for accommodation items
  const roomMix = item.item_type === 'accommodation' ? allocationSummary((item as any).room_allocations) : '';
  const occupancySuffix = roomMix
    ? ` (${roomMix})`
    : item.item_type === 'accommodation' && item.occupancy_type && item.accommodation_type !== 'villas'
    ? ` (${item.occupancy_type.charAt(0).toUpperCase() + item.occupancy_type.slice(1)})`
    : '';
  // S&T suffix — show which staff member this fee covers (Driver / Guide / Other)
  const isST = item.item_type === 'universal_fee' && item.universal_fee_kind === 'st';
  const staffCat = isST ? formatStaffCategory(item as any) : '';
  const stSuffix = isST && staffCat ? ` — ${staffCat}` : '';
  const isChef = item.item_type === 'universal_fee' && item.universal_fee_kind === 'chef';
  const chefPrefix = isChef ? 'Private Chef — ' : '';
  const displayTitle = chefPrefix + baseTitle + occupancySuffix + stSuffix;
  
  const displayDescription = isCustomItem
    ? item.custom_description
    : item.description;

  // Resolve the website link: per-item override → linked supplier's website.
  const rawWebsite = (item.website_url || item.suppliers?.website || "").trim();
  const websiteHref = rawWebsite
    ? (/^https?:\/\//i.test(rawWebsite) ? rawWebsite : `https://${rawWebsite}`)
    : null;
  const websiteLabel = rawWebsite.replace(/^https?:\/\//i, "").replace(/\/$/, "");

  // Handle custom item images (including default placeholders)
  let finalImageUrl = imageUrl;
  if (isCustomItem && item.manual_image_url) {
    if (item.manual_image_url.startsWith('default-')) {
      // It's a default icon placeholder - we'll show a styled placeholder
      finalImageUrl = null;
    } else {
      // It's an uploaded custom image
      finalImageUrl = getStorageUrl(item.manual_image_url);
    }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-background rounded-lg border">
      {/* Left Column - Details (60%) */}
      <div className="md:col-span-3 space-y-2">
        {/* Header row with type badge and location */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="px-3 py-1 bg-primary text-primary-foreground text-xs rounded-full capitalize">
            {item.item_type.replace("_", " ")}
          </span>
          {isST && staffCat && (
            <span className="px-3 py-1 bg-secondary text-secondary-foreground text-xs rounded-full">
              {staffCat}
            </span>
          )}
          {item.location && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-3 w-3" />
              {item.location}
            </span>
          )}
        </div>

        {/* Title */}
        <h4 className="text-lg font-semibold">{displayTitle}</h4>

        {/* Time slot display */}
        {item.selected_time_slot && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            <span>
              {formatTimeSlot(item.selected_time_slot.start)} - {formatTimeSlot(item.selected_time_slot.end)}
            </span>
          </div>
        )}

        {/* Description */}
        {displayDescription && (
          <RichTextDisplay value={displayDescription} className="text-sm text-muted-foreground" />
        )}

        {/* Supplier website */}
        {websiteHref && (
          <a
            href={websiteHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline break-all"
          >
            <Globe className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{websiteLabel}</span>
          </a>
        )}

        {/* Accommodation room type (from supplier room rate) */}
        {item.item_type === "accommodation" && (
          <div className="flex items-center gap-1.5 text-sm font-medium">
            <BedDouble className="h-3.5 w-3.5 text-primary flex-shrink-0" />
            <span>{[item.room_rate?.room_type, roomCountLabel(item as any)].filter(Boolean).join(" · ")}</span>
          </div>
        )}

        {/* Accommodation check-in/check-out dates */}
        {item.item_type === "accommodation" && (item.check_in || item.check_out) && (
          <div className="flex gap-4 text-sm">
            {item.check_in && (
              <span className="text-muted-foreground">
                Check-in: {item.check_in}
              </span>
            )}
            {item.check_out && (
              <span className="text-muted-foreground">
                Check-out: {item.check_out}
              </span>
            )}
            {item.check_in && item.check_out && (() => {
              const nights = calculateAccommodationNights(item.check_in, item.check_out);
              if (!nights) return null;
              return (
                <span className="text-muted-foreground font-medium">
                  · {nights} {nights === 1 ? "night" : "nights"}
                </span>
              );
            })()}
          </div>
        )}

        {/* Universal-fee service window (Guide / S&T / Private Chef) */}
        {item.item_type === "universal_fee" &&
          (item.universal_fee_kind === "guiding" || item.universal_fee_kind === "assistant_guide" ||
            item.universal_fee_kind === "st" ||
            item.universal_fee_kind === "chef") &&
          (item as any).service_start &&
          (item as any).service_end && (() => {
            const start = (item as any).service_start as string;
            const end = (item as any).service_end as string;
            const days = calculateUniversalFeeDays(start, end);
            const qty = item.quantity || 1;
            const showQty = item.universal_fee_kind !== "chef" && qty > 1;
            return (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <CalendarRange className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                <span>
                  {start} → {end} · {days} {days === 1 ? "day" : "days"}
                  {showQty ? ` × ${qty}` : ""}
                </span>
              </div>
            );
          })()}

        {/* Accommodation inclusions (from supplier room rate) */}
        {item.item_type === "accommodation" && (() => {
          const inclusionText = (item.room_rate?.notes || item.room_rate?.description || "").trim();
          if (!inclusionText) return null;
          return (
            <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
              <Check className="h-3.5 w-3.5 mt-0.5 text-primary flex-shrink-0" />
              <span>{inclusionText}</span>
            </div>
          );
        })()}

        {/* Explicit per-item inclusions */}
        {(() => {
          const lines = (((item as any).inclusions || []) as string[])
            .map((l) => (l || "").trim())
            .filter(Boolean);
          if (!lines.length) return null;
          return (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Includes</p>
              <ul className="space-y-1">
                {lines.map((line, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-sm text-muted-foreground">
                    <Check className="h-3.5 w-3.5 mt-0.5 text-primary flex-shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}

        {/* Explicit per-item exclusions */}
        {(() => {
          const lines = (((item as any).exclusions || []) as string[])
            .map((l) => (l || "").trim())
            .filter(Boolean);
          if (!lines.length) return null;
          return (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Excludes</p>
              <ul className="space-y-1">
                {lines.map((line, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-sm text-muted-foreground">
                    <X className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}

      </div>

      {/* Right Column - Image (40%) */}
      <div className="md:col-span-2 flex flex-col gap-2">
        {finalImageUrl ? (
          <div className="relative h-48 rounded-lg overflow-hidden">
            <img
              src={finalImageUrl}
              alt={displayTitle}
              className="w-full h-full object-cover"
            />
          </div>
        ) : item.manual_image_url?.startsWith('default-') ? (
          <div className="relative h-48 rounded-lg overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <div className="text-center">
              <div 
                className="inline-flex items-center justify-center w-16 h-16 min-w-[4rem] rounded-full bg-primary/10 mb-2"
                style={{ 
                  printColorAdjust: 'exact', 
                  WebkitPrintColorAdjust: 'exact' 
                } as React.CSSProperties}
              >
                {(() => {
                  const IconComponent = getDefaultIcon(item.manual_image_url!);
                  const iconColor = getIconColor(item.manual_image_url!);
                  return (
                    <IconComponent 
                      className={`w-8 h-8 ${iconColor} print:block print:visible`}
                      style={{ 
                        printColorAdjust: 'exact',
                        WebkitPrintColorAdjust: 'exact'
                      } as React.CSSProperties}
                    />
                  );
                })()}
              </div>
              <p className="text-xs font-medium text-muted-foreground line-clamp-2">
                {displayTitle}
              </p>
            </div>
          </div>
        ) : (
          <div
            className={`h-48 rounded-lg ${getItemTypeColor(item.item_type)} flex items-center justify-center`}
          >
            <span className="text-white text-sm font-medium capitalize">
              {item.item_type.replace("_", " ")}
            </span>
          </div>
        )}

        {/* Additional images (up to 3): item's own, else product gallery, else supplier gallery */}
        {(() => {
          const anyItem = item as any;
          const pick = (arr: (string | null | undefined)[]) =>
            arr.filter((p): p is string => !!p && p !== rawImagePath);
          const itemExtras = pick([item.secondary_image_url, anyItem.image_url_3, anyItem.image_url_4]);
          const productExtras = pick([anyItem.products?.secondary_image_url, anyItem.products?.image_url_3, anyItem.products?.image_url_4]);
          const supplierExtras = pick([item.suppliers?.secondary_image_url, item.suppliers?.image_url_3, anyItem.suppliers?.image_url_4]);
          const extras = Array.from(new Set(
            itemExtras.length ? itemExtras : productExtras.length ? productExtras : supplierExtras
          ));
          return extras.map((path) => (
            <div key={path} className="relative h-48 rounded-lg overflow-hidden">
              <img src={getStorageUrl(path)} alt="" className="w-full h-full object-cover" />
            </div>
          ));
        })()}
      </div>
    </div>
  );
}
